import { captureException } from "@sentry/react";

import { PregnantSelection } from "@/constants";
import { SurveyAnswers } from "@/contexts";
import { convertLbToKg } from "@/utils/UnitUtils";
import { getMonthDelta, shiftDays, shiftMonths } from "@/utils/datetime";

import { getAdjustedTime } from "./planTimeAdjustment";

/**
 * Calculating target date:
 * 1. Calculate user pace. This value represents the raw per-week expected weight loss
 * 2. Using raw pace, calculate an adjusted target date. This adjustment normalizes target dates,
 *    particularly for raw paces on the extreme ends. See planTimeAdjustment.ts for more info.
 */

/*
FORMULA FOR CALCULATING PACE
IMPORTANT: THIS GIVES US A VALUE IN LBS/WEEK WHICH WE MUST CONVERT AND STORE AS KG/WEEK
pace = I + W * <WeightKG> 

I: intercept, all users start here
W: coefficient for users' weight in kg

Source for these numbers: https://github.com/noom/data-analysis/blob/master/notebooks/craig/wl-graph-shape/parameters.py
*/

const I = 0.03043;
const W = 0.0107018;

const MIN_PACE_IN_LBS_PER_WEEK = 1;
const NURSING_PACE_IN_LBS_PER_WEEK = 1;
const MAX_PACE_IN_LBS_PER_WEEK = 2;
const MAX_PACE_LESS_THAN_5KG_IN_LBS_PER_WEEK = 1.5;

// IMPORTANT: this function takes in kg values as inputs but uses a formula that spits
// out an answer in lbs/week.
// This is the I + W * <WeightKG>
function calculateRawPaceInLbs(surveyAnswers: SurveyAnswers) {
  const { weight } = surveyAnswers.heightWeight ?? {};

  // if we can't capture weightKg value, just return max pace
  if (!weight) {
    return MAX_PACE_IN_LBS_PER_WEEK;
  }

  const weightKg = convertLbToKg({
    mainUnitValue: weight,
  }).mainUnitValue;

  return I + W * weightKg;
}

const getWeightInformation = (surveyAnswers: SurveyAnswers) => {
  const { heightWeight, idealWeight } = surveyAnswers;
  const { weight } = heightWeight || {};

  if (!idealWeight || !weight) {
    captureException(
      Error(
        "Missing weight or idealWeight when calculating target weight loss date",
      ),
    );
    return {};
  }

  const targetWeightKg = convertLbToKg({
    mainUnitValue: idealWeight,
  }).mainUnitValue;
  const weightValueKg = convertLbToKg({
    mainUnitValue: weight,
  }).mainUnitValue;

  return { targetWeightKg, weightValueKg };
};

export function calculatePaceInKgPerWeek(surveyAnswers: SurveyAnswers) {
  const { weightValueKg, targetWeightKg } = getWeightInformation(surveyAnswers);
  if (!weightValueKg || !targetWeightKg) {
    captureException(
      Error("Missing weight or targetWeight when calculating adjusted pace"),
    );
    return undefined;
  }
  const weightDifference = weightValueKg - targetWeightKg;

  // I + W * <WeightKG>
  const paceInLbsPerWeek = calculateRawPaceInLbs(surveyAnswers);

  // Make sure pace is inbetween min and max pace values
  const adjustedPaceInLbsPerWeek = Math.max(
    Math.min(
      paceInLbsPerWeek,
      weightDifference < 5
        ? MAX_PACE_LESS_THAN_5KG_IN_LBS_PER_WEEK
        : MAX_PACE_IN_LBS_PER_WEEK,
    ),
    MIN_PACE_IN_LBS_PER_WEEK,
  );

  // Convert units to kg since this is how the data will be consumed
  const adjustedPaceInKgPerWeek = convertLbToKg({
    mainUnitValue: adjustedPaceInLbsPerWeek,
  }).mainUnitValue;

  if (surveyAnswers.pregnant === PregnantSelection.NO_BUT_NURSING) {
    return convertLbToKg({
      mainUnitValue: NURSING_PACE_IN_LBS_PER_WEEK,
    }).mainUnitValue;
  }

  return adjustedPaceInKgPerWeek;
}

export function getPersonalizedTargetDate(surveyAnswers: SurveyAnswers) {
  const { weightValueKg, targetWeightKg } = getWeightInformation(surveyAnswers);
  if (!weightValueKg || !targetWeightKg) {
    captureException(
      Error(
        "Missing weight or targetWeight when calculating target weight loss date",
      ),
    );
    return undefined;
  }

  const paceInKgPerWeek = calculatePaceInKgPerWeek(surveyAnswers);
  if (!paceInKgPerWeek) {
    captureException(
      Error("Missing pace when calculating target weight loss date"),
    );
    return undefined;
  }

  const currentDate = new Date();

  // Get target num days for user based on weight loss target and pace
  const targetWeightLossInKg = Math.max(weightValueKg - targetWeightKg, 0);
  const numDays =
    surveyAnswers.pregnant === PregnantSelection.NO_BUT_NURSING
      ? Math.round((targetWeightLossInKg / paceInKgPerWeek) * 7)
      : getAdjustedTime(paceInKgPerWeek, targetWeightLossInKg);

  const targetWeightDate = new Date(shiftDays(numDays, currentDate).getTime());

  const monthDelta = getMonthDelta(currentDate, targetWeightDate);
  // If the target date is less than 1 month from now, set it to 1 month from now
  if (monthDelta < 1) {
    return shiftMonths(1, currentDate);
  }

  return targetWeightDate;
}
